import { z, type ZodType } from 'zod'

const CSV_FORMULA_PREFIX_PATTERN = /^\s*[=+\-@]/
const SINGLE_LINE_BREAK_PATTERN = /[\r\n]/

function containsDisallowedControlChars(value: string) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (
      (code >= 0x00 && code <= 0x08) ||
      code === 0x0b ||
      code === 0x0c ||
      (code >= 0x0e && code <= 0x1f) ||
      code === 0x7f
    ) {
      return true
    }
  }
  return false
}

type BaseTextSchemaOptions = {
  required?: boolean
  minLength?: number
  maxLength?: number
  multiline?: boolean
  disallowCsvFormula?: boolean
}

type FieldSchemaOptions = BaseTextSchemaOptions

function stringValue(input: unknown) {
  return typeof input === 'string' ? input : String(input ?? '')
}

function withTextRules(
  label: string,
  options: BaseTextSchemaOptions = {}
) {
  const {
    required = true,
    minLength,
    maxLength,
    multiline = false,
    disallowCsvFormula = true,
  } = options

  let schema = z.string().trim()

  if (required) {
    schema = schema.min(1, `${label} is required.`)
  }

  if (minLength && minLength > 1) {
    schema = schema.refine(
      (value) => value.length === 0 || value.length >= minLength,
      `${label} must be at least ${minLength} characters long.`
    )
  }

  if (maxLength) {
    schema = schema.max(maxLength, `${label} must be ${maxLength} characters or fewer.`)
  }

  schema = schema.refine(
    (value) => !value || !containsDisallowedControlChars(value),
    `${label} contains unsupported control characters.`
  )

  if (!multiline) {
    schema = schema.refine(
      (value) => !value || !SINGLE_LINE_BREAK_PATTERN.test(value),
      `${label} must be a single line.`
    )
  }

  if (disallowCsvFormula) {
    schema = schema.refine(
      (value) => !value || !CSV_FORMULA_PREFIX_PATTERN.test(value),
      `${label} cannot start with spreadsheet formula characters.`
    )
  }

  return schema
}

export function personNameSchema(label: string, options: FieldSchemaOptions = {}) {
  return withTextRules(label, {
    minLength: 2,
    maxLength: 50,
    ...options,
  })
}

export function entityNameSchema(label: string, options: FieldSchemaOptions = {}) {
  return withTextRules(label, {
    maxLength: 100,
    ...options,
  })
}

export function shortTextSchema(label: string, options: FieldSchemaOptions = {}) {
  return withTextRules(label, {
    maxLength: 255,
    ...options,
  })
}

export function longTextSchema(label: string, options: FieldSchemaOptions = {}) {
  return withTextRules(label, {
    required: false,
    multiline: true,
    maxLength: 2000,
    ...options,
  })
}

export function requiredEmailSchema(label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .max(320, `${label} exceeds maximum allowed length.`)
    .refine((value: string) => !SINGLE_LINE_BREAK_PATTERN.test(value), `${label} must be a single line.`)
    .email(`${label} format is invalid.`)
}

export function zodAntdValidator(schema: ZodType<string>) {
  return async (_rule: unknown, value: unknown) => {
    const result = schema.safeParse(stringValue(value))
    if (!result.success) {
      throw new Error(result.error.issues[0]?.message || 'Invalid value.')
    }
  }
}

export function hasCsvFormulaPrefix(value: string) {
  return CSV_FORMULA_PREFIX_PATTERN.test(value)
}
